import { useEffect, useState } from "react";
import { Image } from "@/components/Image";
import { useWorkshopMedia } from "../api/useWorkshopMedia";
import { pictureHome } from "../workshopThread";
import { OpenBesideLabel } from "./WorkshopOutputCard";
import { KindTag } from "./WorkshopParts";

type Props = {
  projectId: string;
  /** The tool result's id: a project image, or (older turns) a library image. */
  mediaId: string;
  fallbackFileName: string | null;
  fallbackAspectRatio: string | null;
  onOpen?: (mediaId: string) => void;
};

/**
 * The thumbnail is rendered by the worker a few seconds AFTER GenerateImage
 * returns, so the refetch the tool call triggers always lands too early to see
 * it. The media query is deliberately never polled — its presigned URLs
 * rotate on every fetch — so this card, the one surface showing a
 * seconds-old image, nudges it a bounded number of times and then stops for
 * good (a job that failed must not retry forever).
 */
const THUMB_POLL_MS = 4000;
const THUMB_POLL_TRIES = 4;

function ratioOf(width: number | null | undefined, height: number | null | undefined, fallback: string | null) {
  if (width && height) return `${width} / ${height}`;
  if (fallback === "3:2") return "3 / 2";
  if (fallback === "2:3") return "2 / 3";
  return "1 / 1";
}

/**
 * A picture the agent made here, in the chat: rounded (media is the one rounded
 * thing in Workshop), IMAGE (a picture is its own record, so the tag is a fact),
 * its name, Open beside, and where it really lives, read from its record. A turn
 * from before project pictures kept their own records names a library id; when
 * the project has no such picture the card says so instead of guessing.
 */
export default function GeneratedMediaPreviewCard({
  projectId,
  mediaId,
  fallbackFileName,
  fallbackAspectRatio,
  onOpen,
}: Props) {
  const { data, refetch, isLoading } = useWorkshopMedia(projectId);
  const item = data?.find((m) => m.id === mediaId || m.copiedFromBrandMediaId === mediaId);

  const awaitingThumb = Boolean(item && !item.thumbUrl);
  // Once the nudges run out the frame stops pulsing: nothing more is coming.
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!awaitingThumb) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      void refetch();
      if (tries >= THUMB_POLL_TRIES) {
        window.clearInterval(timer);
        setGaveUp(true);
      }
    }, THUMB_POLL_MS);
    return () => window.clearInterval(timer);
  }, [awaitingThumb, refetch]);

  const name = item?.fileName?.trim() || fallbackFileName?.trim() || "Generated picture";
  const src = item ? (item.thumbUrl ?? item.url) : null;
  const missing = !isLoading && data !== undefined && !item;
  const open = onOpen && item ? () => onOpen(item.id) : undefined;

  return (
    <figure className="ws-pic">
      <div
        className="ws-pic-frame"
        style={{ aspectRatio: ratioOf(item?.width, item?.height, fallbackAspectRatio) }}
        data-waiting={!src && !missing && !gaveUp ? true : undefined}
      >
        {src ? <Image src={src} alt={name} draggable={false} /> : null}
        {open ? (
          // A pointer target only: Open beside below is the one the keyboard reaches.
          <button
            type="button"
            className="ws-pic-hit"
            onClick={open}
            tabIndex={-1}
            aria-hidden="true"
          />
        ) : null}
      </div>
      <figcaption className="ws-pic-cap">
        <KindTag>Image</KindTag>
        <span className="ws-pic-name">{name}</span>
        {open ? (
          <button type="button" className="ws-out-open" onClick={open} aria-label={`Open ${name}`}>
            <OpenBesideLabel />
          </button>
        ) : null}
      </figcaption>
      {item ? (
        <p className="ws-pic-home">{pictureHome(item.library)}</p>
      ) : missing ? (
        <p className="ws-pic-home">Not among this project’s pictures.</p>
      ) : null}
    </figure>
  );
}
